import { Grid, List, ListItem, ListItemIcon, ListItemText, Stack, Typography, useTheme } from '@mui/material';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import FullScreenDialog from '../full-screen-dialog/FullScreenDialog';
import SettingsBar, { SettingsActionEnum } from '../settings-bar/SettingsBar';
import UnsavedChangesPrompt from '../unsaved-changes-prompt/UnsavedChangesPrompt';
import Form from '../forms/form/Form';
import { useQueryNotifications } from '../../queries/UseQueryNotifications';
import { useUserSettings } from '../../queries/user-settings/UseUserSettings';
import { useForm } from 'react-hook-form';
import { UserStateActionTypeEnum, useUserContext } from '../../contexts/user-context/UserContext';
import { useSaveUserProfile } from '../../queries/user-settings/UseSaveUserProfile';
import { IeLynxUser } from '../../common/models/eLynxUser';
import { useUserRecipients } from '../../queries/user-role-administration/UseUserRecipients';
import { useRouteContext } from '../../hooks/use-route-context/UseRouteContext';
import { IRecipient } from '../../common/models/IRecipient';
import { RecipientTypeEnum } from '../../common/enums/RecipientTypeEnum';
import { Phone, Sms } from '@mui/icons-material';
import AccountProfileV2Utils, { AccountProfileV2FormType, defaultAccountProfileV2 } from './AccountProfileV2Utils';
import UserProfileCard from './UserProfileCard';
import UserNotificationsCard from './UserNotificationsCard';
import { useUser } from '../../queries/user-role-administration/UseUser';
import UserApiCard from './UserApiCard';

export type AccountProfileV2Props = {
    open: boolean;
    onClose: () => void
};

export default function AccountProfileV2({open, onClose}: AccountProfileV2Props) {
    
    const { t } = useTranslation();
    const theme = useTheme();
    const [offlineState] = useOfflineDetectContext();
    const { confirm, acknowledge } = useConfirmation();
    const routeContext = useRouteContext();
    const [userState, dispatchUserState] = useUserContext();
    const { data: userSettings } = useQueryNotifications(useUserSettings)({
        enabled: !offlineState.offline
    });

    const {data: userEntity } = useQueryNotifications(useUser)({
        groupID: routeContext.groupID as number,
        userID: userState.user!.id,
        enabled: !offlineState.offline
    });
    
    const { data: userRecipients, refetch: refetchRecipients } = useQueryNotifications(useUserRecipients)({
        groupID: routeContext.groupID as number,
        userID: userState.user?.id as number,
        enabled: !offlineState.offline && open
    });

    const { mutate: saveUserProfile } = useQueryNotifications(useSaveUserProfile)({
        onSuccess: (data) => {
            // Now that we've got the finalized profile data back, update
            // the state accordingly. This profile may contain additional
            // information beyond what we sent. Specifically, this may happen
            // with regard to some of the MFA information.
            dispatchUserState({
                type: UserStateActionTypeEnum.UserChanged,
                user: {...data,
                    baseGroup: userState.user?.baseGroup
                }
            });

            refetchRecipients();

            reset({...defaultAccountProfileV2}, {
                keepDirty: false, 
                keepDirtyValues: false
            });

        }
    });

    const form = useForm<AccountProfileV2FormType>({
        //mode: "all",
        mode: 'onChange',
        defaultValues: {...defaultAccountProfileV2
        }
    });

    const {
        getValues,
        //watch,
        //trigger,
        formState: {
            dirtyFields,
            isDirty,
            isValid,
            isSubmitting
            //defaultValues
            //touchedFields
        },
        reset,
        //setValue,
        //control,
        //handleSubmit,
    } = form;

    const trimPhoneNumber = (value: string) => value.replace('+1', '').replace(/[-()+]/g, '').trim();

    const onCloseSettings = async (action: SettingsActionEnum): Promise<void> => {

        if (action === SettingsActionEnum.OK) {

            const formValues = getValues();

            if (AccountProfileV2Utils.hasProfileChanges(dirtyFields)) {


                const phoneNumber = formValues.phone.replace(/-/g, '')?.trim();                

                // Check if the phone number has changed and if so, check if it's used for any alarm notification recipients
                // If it is, then prompt the user to update the recipients
                if (phoneNumber !== userState.user!.phoneNumber) {

                    const matchingRecipients = (userRecipients ?? []).filter(o => o.recipientType !== RecipientTypeEnum.Email && trimPhoneNumber(o.to) === trimPhoneNumber(userState.user!.phoneNumber ?? ''));

                    if (matchingRecipients.length > 0) {
        
                        const result = await confirm({
                            dialogTitle: t("userEditor.steps.general.updateRecipients.title"),
                            yesButtonText: t("userEditor.steps.general.updateRecipients.yesButtonText"),
                            noButtonText: t("userEditor.steps.general.updateRecipients.noButtonText"),
                            showConfirmationText: false,
                            confirmationBodyComponent: 
                            <Stack direction='column' spacing={2}>
                                <Typography variant="body2">
                                    {t("userEditor.steps.general.updateRecipients.confirmationText1")}
                                </Typography>
                                <List dense={true}>
                                    {matchingRecipients.map((o, ndx) => (
                                    <ListItem key={ndx}>
                                        <ListItemIcon sx={{minWidth: 'auto', marginRight: theme.spacing(1)}}>
                                            {o.recipientType === RecipientTypeEnum.Voice ? <Phone /> : <Sms />}
                                        </ListItemIcon>
                                        <ListItemText primary={o.to} />
                                    </ListItem>
                                    ))}
                                </List>
        
                                <Typography variant="body2">
                                    {t("userEditor.steps.general.updateRecipients.confirmationText2")}
                                </Typography>
        
                              </Stack>,
                            showYesButton: true,
                            showNoButton: true,
                            showCloseButton: true
                        });
        
                        if (result === 'close') {
                            return;
                        }
        
                        if (result === 'yes') {
        
                            const newRecipients = [...formValues.recipients];
        
                            matchingRecipients.forEach(m => {
                                const index = newRecipients.findIndex(o => o.to === m.to);
                                if (index !== -1) {
                                    const recipient = newRecipients[index];
                                    const parts = recipient.to.split(' ');
                                    recipient.to = `${parts[0]} ${phoneNumber}`;
        
                                    newRecipients[index] = recipient
                                }
                            });
        
                            formValues.recipients = newRecipients;
                        }
                    }
                }

                const model = AccountProfileV2Utils.mapToProfileModel(formValues, userState.user as IeLynxUser, userRecipients as IRecipient[]);
                saveUserProfile(model);
            }

            acknowledge(t('profile.profileUpdated'));

            reset({...defaultAccountProfileV2}, {
                keepDirty: false, 
                keepDirtyValues: false
            });


        } else if (isDirty) {
            const result = await getUserConfirmation();
            if (!result)  {
                return;
            }
        }

        onClose();        
    }

    const getUserConfirmation = async () => {

        return (await confirm({
            dialogTitle: t('cancelConfirmation.title'),
            confirmationText: t('profile.cancelEditText'),
            yesButtonText: t('cancelConfirmation.yesButtonText'),
            noButtonText: t('cancelConfirmation.noButtonText'),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";
    };


    useEffect(() => {

        if (open) {

            let formData: AccountProfileV2FormType | undefined = undefined;

            if (offlineState.offline && userState.user) {

                formData = AccountProfileV2Utils.mapToForm(userState.user,
                    undefined,
                    undefined,
                );

            } else if (userState.user && userSettings && userEntity) {

                const recipients = userEntity?.recipients?.map(o => {
                    return {

                        id: o.id,
                        recipientType: o.type,
                        recipientSubType: o.subType,
                        to: o.to,
                        disabled: o.disabled,
                        disabledTimestamp: o.disabledTimestamp,
                        disabledSource: o.disabledSource,
                        usageCount: o.usageCount

                    } as IRecipient;
                });

                formData = AccountProfileV2Utils.mapToForm(userState.user,
                    userSettings,
                    recipients,
                    );
            }

            if (formData) {
                reset(formData, {
                    keepDirty: true, 
                    keepDirtyValues: true
                });
            }
        }
    }, [
        userState.user,
        userEntity,
        userSettings,
        reset,
        offlineState.offline,
        open,
    ]);

    return (

        <FullScreenDialog open={open} onClose={onClose}>

                <SettingsBar title={t('profile.myProfile')} 
                    onClose={onCloseSettings}
                    modified={isDirty && isValid  && !isSubmitting} />

                <Form form={form}
                    sx={{
                        backgroundColor: "custom.background.cardContainer",
                    }}
                >

                    <UserProfileCard />

                    {
                    !offlineState.offline &&

                        <Grid container spacing={2}>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <UserNotificationsCard />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <UserApiCard />
                            </Grid>

                        </Grid>
                    }

                </Form>

                <UnsavedChangesPrompt
                    hasChanges={isDirty}
                    getUserConfirmation={getUserConfirmation}
                />


        </FullScreenDialog>
        
    );
  }
